<template>
    <div class="filter-panel">
        <SearchListHeadline :headline="headline" />
        <ResultsAndSorter class="filter-panel__result" :canShowListToggleView="isMobileView"/>
        <MobileFilters
            v-if="isMobileView"
            class="filter-panel__filters filter-panel__mobile-filters"
            :class="classes"
            :style="styles"
            ref="filters"
        />
        <DesktopFilters
            v-if="isDesktopView"
            class="filter-panel__filters -wrap"
            :class="classes"
            :style="styles"
            ref="filters"
        />
        <Tags class="filter-panel__tags" :tags="filterTags" :showInfoIcon="showInfoIcon"/>
        <SearchListViewTypeTabs :isPackageCustomerCustom="isPackageCustomerCustom" v-if="!$isLangDk"/>
        <ExpandSearch
            :resultsOutsideSearchQuery="resultsOutsideSearchQuery"
            :filteredCount="filteredCount"
             v-if="shouldHintButtonToSwitchCategoryBeenDisplayed"
        />
        <TotemOverlay v-show="showFiltersOverlay" @click="hideMoreFilters"  shouldCoverSidebar/>
    </div>
</template>

<script>
    import { createNamespacedHelpers, mapGetters } from 'vuex'
    import debounce from 'components/shared/mixins/debounce'
    import SearchListHeadline from 'components/searchable-listing/filters-elements/SearchListHeadline'
    import Tags from 'components/searchable-listing/filters-elements/Tags'
    import ResultsAndSorter from 'components/searchable-listing/filters-elements/ResultsAndSorter'
    import ExpandSearch from 'components/searchable-listing/filters-elements/ExpandSearch'
    import MobileFilters from 'components/searchable-listing/filters-elements/MobileFilters'
    import DesktopFilters from 'components/searchable-listing/filters-elements/DesktopFilters'
    import SearchListViewTypeTabs from 'components/searchable-listing/filters-elements/fields/SearchListViewTypeTabs'
    const { mapState } = createNamespacedHelpers('Search')

    export default {
        name: 'SearchCourseListFilter',
        components: {
            DesktopFilters,
            MobileFilters,
            ResultsAndSorter,
            Tags,
            SearchListHeadline,
            ExpandSearch,
            SearchListViewTypeTabs
        },
        props: {
            courseList: Array,
            headline: {
                type: Object
            }
        },
        mixins: [
            debounce
        ],
        data () {
            return {
                currentPackageSlug: null,
                filterPanelSelectsHeight: null,
                shouldStartFilterTransitions: false,
            }
        },
        computed: {
            ...mapGetters({
                tags: 'Search/tags',
                showMoreFilters: 'CourseFilter/getShowMoreFilterState',
                viewTypeCounts: 'Search/viewTypeCounts',
                filterStaticData: 'Search/getFilterStaticData',
                shouldHintButtonToSwitchCategoryBeenDisplayed: 'Search/shouldHintButtonToSwitchCategoryBeenDisplayed',
                resultsOutsideSearchQuery: 'Search/getOutsidePackageScopeCount',
                filteredCount: 'Search/filteredCount',
                categories: 'Menu/getCategories',
            }),
            ...mapState({
                filters: state => state.filters,
                filteredCount: state => state.filteredCount,
                mainPackage: state => state.selectedMainPackage
            }),
            showInfoIcon () {
                // TODO: reimplement logic below
                // return this.selectedOptions.length > 0 || this.selectedRibbon !== null
                return false
            },
            classes () {
                return [
                    { 'show-filters': this.showMoreFilters },
                    { 'start-transitions': this.shouldStartFilterTransitions }
                ]
            },
            styles () {
                if(this.showMoreFilters) {
                    return { marginTop: 0, opacity: 1 }
                } else {
                    return { marginTop: -this.stylesValues.filterSelectsHeight - 30 + 'px', opacity: this.stylesValues.opacity }
                }
            },
            stylesValues () {
                if (this.isMobileView) {
                    return { filterSelectsHeight: 0, opacity: 1 }
                }
                return this.showMoreFilters ?
                    { filterSelectsHeight: 0, opacity: 1 } :
                    { filterSelectsHeight: this.filterPanelSelectsHeight, opacity: 0 }
            },
            showFiltersOverlay () {
                return this.isMobileView && this.showMoreFilters
            },
            filterTags () {
                return this.tags.map(tag => ({ ...tag, translatedLabel: this.getTranslationForTag(tag) }))
            },
            isPackageCustomerCustom () {
                const category = this.categories.find(category => category.code === this.mainPackage)
                return category?.customerSpecific
            }
        },
        methods: {
            getTranslationForTag (tag) {
                if (!this.filterStaticData[tag.filterKey]['labels']) {
                    return tag.label
                }
                return this.filterStaticData[tag.filterKey]['labels'][tag.label]
            },
            getFilterSelectsHeight () {
                const panel = this.$refs?.filters.getFilterPanelSelects()
                if (panel) {
                    this.filterPanelSelectsHeight = panel
                }
            },
            hideMoreFilters () {
                this.$store.dispatch('CourseFilter/showMoreFilters', false)
            },
        },
        watch: {
            showMoreFilters: {
                handler () {
                    this.shouldStartFilterTransitions = true
                    setTimeout(() => {
                        this.shouldStartFilterTransitions = false
                    }, 400)
                }
            }
        },
        mounted () {
            window.addEventListener('resize', this.getFilterSelectsHeight)
            this.getFilterSelectsHeight()
        },
        beforeDestroy () {
            this.$store.dispatch('CourseFilter/assignCurrentPackageSlug', this.currentPackageSlug)

            if (this.$route.name !== 'course-list-course-page') {
                this.$store.dispatch('CourseFilter/clearFilter')
            }
            window.removeEventListener('resize', this.getFilterSelectsHeight)
        }
    }
</script>

<style scoped lang="scss">
    .filter-panel {
        display: flex;
        flex-direction: column;
        position: relative;

        &__filters {
            padding: 0;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
            position: relative;
            transition: none;

            &.show-filters {
                margin-top: 0;
            }

            &.start-transitions {
                transition: margin-top 0.3s ease-in-out, opacity 0.3s ease;
            }
        }

        &__mobile-filters {
            position: fixed;
            top: 0;
            bottom: 0;
            display: block;
            background: $white;
            z-index: 1000;
            width: 320px;
            right: -320px;
            transition: none;
            margin: 0;
            padding: 20px;

            [class*="mobile_md"] & {
                width: 500px;
                right: -500px;
            }

            &.show-filters {
                right: 0;
                margin: 0;
            }

            &.start-transitions {
                transition: right 0.3s ease-in-out;
            }
        }

        &__selects {
            display: flex;
            flex-direction: row;
            flex: 1;
            flex-wrap: wrap;
            justify-content: flex-start;
        }

        &__label {
            display: inline-block;
            margin: 20px 0;

            [class*="desktop"] & {
                margin: 8px 20px 0 0;
            }
        }

        &__result {
            background: $white;
            display: flex;
            align-items: center;
            flex-direction: column;
            z-index: 999;
            margin: 0 0 30px 0;

            [class*="desktop"] & {
                background: $white;
                flex-direction: row;
                justify-content: space-between;
            }

            [class*="mobile"] & {
                flex-wrap: wrap;
                flex-direction: row;
                gap: 15px;
                margin: 0
            }
        }

        &__tags {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            margin: 20px 0 15px;
        }

        &__sort-toggle {
            display: flex;
            width: 100%;
            justify-content: space-between;
        }
        .icon-clear {
            font-size: 2.4rem;
            color: $carnation;
            margin: 10px 0;

            [class*="desktop"] & {
                margin: 6px 0 0 10px;
            }
        }

        .icon-info {
            font-size: 2rem;
            margin: 0 15px 0 0;
        }
    }
</style>
